<template>
  <div>
    <b-card no-body>
      <b-card-body class="p-4">
        <b-form>
          <h1>
            <span>Register</span>
          </h1>

          <p v-if="error" class="alert alert-warning">{{ error }}</p>

          <p class="text-muted">Let us know who you are.</p>
          <customer-contact-form-group
            :customer_contact="customer_contact"
            :vuelidate="$v.customer_contact"
            @onCustomerContactChange="onCustomerContactChange"
          />
          <hr />
          <p class="text-muted">
            <i class="fal fa-building" />
            <span class="pl-2">Tell us about your company.</span>
          </p>
          <customer-form-group
            :customer="customer"
            @onCustomerChange="onCustomerChange"
          />
          <hr />
          <p class="text-muted">
            <i class="fal fa-map-marker" />
            <span class="pl-2">Where is your company located?</span>
          </p>
          <customer-location-form-group
            :customer_location="customer_location"
            @onCustomerLocationChange="onCustomerLocationChange"
          />

          <hr />

          <p class="text-muted">Your password must be a minimum of 8 characters and include uppercase letters, lowercase letters, special characters, and numbers.</p>

          <b-input-group class="mb-3">
            <b-input-group-prepend>
              <b-input-group-text><i class="icon-lock" /></b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
              v-model="password"
              type="password"
              class="form-control"
              placeholder="Password"
              autocomplete="new-password"
              :state="$v.password.$dirty ? !$v.password.$invalid : null"
            />
            <b-form-invalid-feedback>
              <span v-if="!$v.password.required">Required</span>
              <span v-else-if="!$v.password.minLength">Password must be at least 8 characters long</span>
              <span v-else-if="!$v.password.containsLowercase">Password must have lowercase characters</span>
              <span v-else-if="!$v.password.containsUppercase">Password must have uppercase characters</span>
              <span v-else-if="!$v.password.containsNumber">Password must have numeric characters</span>
              <span v-else-if="!$v.password.containsSpecial">Password must have symbol characters</span>
            </b-form-invalid-feedback>
          </b-input-group>

          <b-input-group class="mb-4">
            <b-input-group-prepend>
              <b-input-group-text><i class="icon-lock" /></b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
              v-model="password_confirm"
              type="password"
              class="form-control"
              placeholder="Repeat password"
              autocomplete="new-password"
              :state="$v.password_confirm.$dirty ? !$v.password_confirm.$invalid : null"
            />
            <b-form-invalid-feedback>
              <span v-if="!$v.password_confirm.required">Please confirm your password</span>
              <span v-else-if="!$v.password_confirm.sameAs">Passwords do not match</span>
            </b-form-invalid-feedback>
          </b-input-group>

          <b-button variant="success" block :disabled="loading" @click="signUp">
            <i v-if="loading" class="fal fa-spinner fa-spin" />
            <span v-else>Create Account</span>
          </b-button>
        </b-form>
      </b-card-body>

    </b-card>
  </div>
</template>

<script>
import { AUTH_REGISTER_REQUEST } from '@/store/action-types'
import CustomerContactFormGroup from './inner/CustomerContactFormGroup'
import CustomerFormGroup from './inner/CustomerFormGroup'
import CustomerLocationFormGroup from './inner/CustomerLocationFormGroup'
import { required, email, sameAs, minLength } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate/src'

export default {
  name: 'RegisterForm',
  components: { CustomerLocationFormGroup, CustomerFormGroup, CustomerContactFormGroup },
  mixins: [validationMixin],
  props: ['custom_error'],
  data () {
    return {
      error: null,
      loading: false,

      customer_contact: null,
      customer_location: null,
      customer: null,

      customer_model: null,

      available_roles: ['Grower', 'Agronomist'],
      available_location_types: ['Shipping', 'Billing'],

      password: null,
      password_confirm: null,

      challenge: null

    }
  },
  watch: {
    custom_error: function (val) {
      this.error = val
    }
  },
  mounted () {
    this.error = (this.custom_error || null)
    if (this.$cookies.get('faker_enabled') === '1') {
      this.fillForm()
    }
    this.$v.$touch()
  },
  validations() {
    return {
      customer_contact: {
        first_name: { required },
        last_name: { required },
        email: { required, email },
      },
      password: {
        required,
        minLength: minLength(8),
        containsUppercase: function(value) {
          return /[A-Z]/.test(value)
        },
        containsLowercase: function(value) {
          return /[a-z]/.test(value)
        },
        containsNumber: function(value) {
          return /[0-9]/.test(value)
        },
        containsSpecial: function(value) {
          return /[#?!@$%^&*-]/.test(value)
        }
      },
      password_confirm: {
        required,
        sameAs: sameAs('password')
      }
    }
  },
  methods: {

    onCustomerContactChange: function (customer_contact) {
      this.customer_contact = customer_contact
    },
    onCustomerChange: function (customer) {
      this.customer = customer
    },
    onCustomerLocationChange: function (customer_location) {
      this.customer_location = customer_location
    },

    signUp: function () {
      this.$v.$touch()
      if (this.$v.$dirty && this.$v.$invalid) return

      this.loading = true

      const registrationData = {
        email: this.customer_contact.email,
        password: this.password,
        contact: {
          first_name: this.customer_contact.first_name,
          last_name: this.customer_contact.last_name,
          phone: this.customer_contact.phone_number || undefined
        },
        customer: {
          role: this.customer_contact.role || undefined,
          ...this.customer,
          address: this.customer_location || undefined
        }
      }

      this.createUser(registrationData).then(() => {
        const status = this.$store.getters.getAuthenticationStatus
        if (status === 'register__success') {
          this.$emit('onUserConfirmed')
        } else if (status === 'register__confirmation') {
          this.$emit('onUserCreated')
        } else {
          this.error = 'Something went wrong please contact an administrator'
        }
        this.loading = false
      }, (err) => {
        this.loading = false
        let responseError = err.response && err.response.data && err.response.data.message
        this.error = responseError || err.err || 'Something went wrong please contact an administrator';
      })
    },

    createUser: function (registrationData) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('AUTH_REGISTER_REQUEST', registrationData)
          .then(resolve)
          .catch(reject)
      })
    },

    fillForm: function () {
      const customer = this.$faker().company.companyName()
      const email = 'kwittmack@gmail.com'
      this.customer = {
        short_name: customer.split(' ')[0],
        full_name: customer
      }
      this.customer_contact = {
        name: this.$faker().name.findName(),
        phone_number: this.$faker().phone.phoneNumber(),
        email: email
      }
      this.customer_location = {
        street_address: this.$faker().address.streetAddress(),
        city: this.$faker().address.city(),
        region: this.$faker().address.state(),
        country: this.$faker().address.country(),
        postal_code: this.$faker().address.zipCode()
      }
      this.password = 'Password1!'
      this.password_confirm = 'Password1!'
    }

  }
}
</script>
